import type { RepresentationSummary } from '../MyDashboard/service';

type RepresentationStaticIds = {
    citizenViewId: string;
    adminViewId: string;
};

const DEFAULT_STATIC_IDS: RepresentationStaticIds = {
    citizenViewId: 'citizen-view',
    adminViewId: 'admin-view',
};

export const normalizeId = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

export const isStaticRepresentationId = (
    value: string | null,
    staticIds: RepresentationStaticIds = DEFAULT_STATIC_IDS,
): boolean => value === staticIds.citizenViewId || value === staticIds.adminViewId;

const stripRepresentationPrefix = (id: string): string => id.replace(/^REPRESENTATION:/i, '');

export const resolvePreferredRepresentationId = (
    current: string | null,
    activeItems: RepresentationSummary[],
    authReady: boolean,
    canUseAdminView: boolean,
    staticIds: RepresentationStaticIds = DEFAULT_STATIC_IDS,
): string => {
    const { citizenViewId, adminViewId } = staticIds;

    if (authReady && current === adminViewId && !canUseAdminView) {
        return citizenViewId;
    }
    if (isStaticRepresentationId(current, staticIds)) {
        return current as string;
    }
    if (!current) {
        return citizenViewId;
    }
    // SMOKE-16 hardening (2026-04-20): se `activeItems` ainda não foi carregado
    // (primeiro render, retry de fetch, erro transiente), preservamos o `current`
    // do localStorage em vez de cair em citizen. Sem isso, qualquer refresh que
    // temporariamente zere `representations` sobrescreveria a seleção do
    // usuário. Quando items chegar, o próximo ciclo do hydrate confirma ou
    // ajusta. Current ainda precisa existir — se null/vazio, caímos em citizen.
    if (!activeItems.length) {
        return current;
    }
    // Comparação tolerante a prefix `REPRESENTATION:` — `loginAsAuthority` do E2E
    // (e outros atalhos) às vezes persistem o id sem prefix enquanto
    // `activeItems[].id` vem do backend com prefix. Sem esta tolerância, o fix
    // silenciosamente caía em `citizenViewId` e o user "autoridade" aparecia
    // como cidadão, quebrando a checagem de role por representation selecionada.
    // Retorna o id canônico do activeItem (com prefix) para normalizar downstream.
    const currentBare = stripRepresentationPrefix(current);
    const match = activeItems.find((item) => stripRepresentationPrefix(item.id) === currentBare);
    return match ? match.id : citizenViewId;
};
